import Message from "@/models/conversation/Message";
import DocumentReference from "@/models/conversation/document";
import { GENERATION_FAILED_STATUS } from "@/lib/chatGenerationErrors";
import type { ChatDoneEvent } from "@/services/chatStreamService";

/** Module-scoped so locks survive React Strict Mode provider remounts. */
const reconcilingGenerationIds = new Set<string>();
const persistingGenerationIds = new Set<string>();
let reconcileInFlight: Promise<void> | null = null;

export const tryAcquireReconcileGenerationLock = (
  generationId: string
): boolean => {
  if (reconcilingGenerationIds.has(generationId)) {
    return false;
  }
  reconcilingGenerationIds.add(generationId);
  return true;
};

export const releaseReconcileGenerationLock = (generationId: string): void => {
  reconcilingGenerationIds.delete(generationId);
};

export const tryAcquirePersistGenerationLock = (
  generationId: string
): boolean => {
  if (persistingGenerationIds.has(generationId)) {
    return false;
  }
  persistingGenerationIds.add(generationId);
  return true;
};

export const releasePersistGenerationLock = (generationId: string): void => {
  persistingGenerationIds.delete(generationId);
};

export const runExclusiveReconcile = (
  task: () => Promise<void>
): Promise<void> => {
  if (reconcileInFlight) {
    return reconcileInFlight;
  }

  reconcileInFlight = (async () => {
    try {
      await task();
    } finally {
      reconcileInFlight = null;
    }
  })();

  return reconcileInFlight;
};

const findMostRecentHumanMessageIndex = (messages: Message[]): number => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index].human) {
      return index;
    }
  }
  return -1;
};

const findLatestClosedAiAfterLatestHuman = (
  messages: Message[]
): Message | undefined => {
  const latestHumanIndex = findMostRecentHumanMessageIndex(messages);

  for (let index = messages.length - 1; index > latestHumanIndex; index -= 1) {
    const message = messages[index];
    if (message.human || !message.isClosed) {
      continue;
    }
    return message;
  }

  return undefined;
};

const haveSameReferenceIds = (
  left: DocumentReference[],
  right: DocumentReference[]
): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  const leftIds = left
    .map((reference) => reference.referenceId)
    .sort((leftId, rightId) => leftId - rightId);
  const rightIds = right
    .map((reference) => reference.referenceId)
    .sort((leftId, rightId) => leftId - rightId);

  return leftIds.every((referenceId, index) => referenceId === rightIds[index]);
};

export const isDoneEventAlreadyPersisted = (
  messages: Message[],
  doneEvent: ChatDoneEvent
): boolean => {
  const latestClosedAi = findLatestClosedAiAfterLatestHuman(messages);
  if (!latestClosedAi) {
    return false;
  }

  return (
    latestClosedAi.text === doneEvent.text &&
    haveSameReferenceIds(latestClosedAi.references, doneEvent.references)
  );
};

export const isFailedStreamAlreadyPersisted = (messages: Message[]): boolean => {
  const latestClosedAi = findLatestClosedAiAfterLatestHuman(messages);
  if (!latestClosedAi) {
    return false;
  }

  return latestClosedAi.statusShort === GENERATION_FAILED_STATUS;
};

export const isSuccessfulStreamAlreadyPersisted = (
  messages: Message[]
): boolean => {
  const latestClosedAi = findLatestClosedAiAfterLatestHuman(messages);
  if (!latestClosedAi) {
    return false;
  }

  return latestClosedAi.statusShort !== GENERATION_FAILED_STATUS;
};
